import { useMemo } from "react";
import { Modal, Table, Button, Typography, Tag, Tooltip } from "antd";
import { EyeOutlined, PlusOutlined, CloseOutlined, ArrowRightOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);

const { Text, Title } = Typography;

const UserTripListModal = ({
  isOpen,
  onClose,
  selectedDate,
  dayTrips,
  onViewTrip,
  onRequestTrip,
  hasActivePosition = true,
  noPositionMessage = "",
}) => {
  const isPastDate = selectedDate ? dayjs(selectedDate).isBefore(dayjs(), 'day') : false;

  const sortedDayTrips = useMemo(() => {
    return [...(dayTrips || [])].sort((a, b) => {
      const getRawTime = (item) => item.pickup_time || item.rawData?.estimated_pickup_time || "00:00:00";
      return getRawTime(a).localeCompare(getRawTime(b));
    });
  }, [dayTrips]);

  const columns = [
    { 
      title: "Pickup", 
      dataIndex: "pickup",
      key: "pickup",
    },
    {
      title: "",
      key: "arrow",
      render: () => <ArrowRightOutlined style={{ color: '#8c8c8c' }} />,
      width: 30
    },
    { 
      title: "Destination", 
      dataIndex: "destination",
      key: "destination",
    },
    { title: "Pickup Time", dataIndex: "time", key: "time" },
    { title: "Estimated Arrival", dataIndex: "estimatedArrival", key: "estimatedArrival" },
    { title: "Unit", dataIndex: "organizing_unit", key: "organizing_unit" },
    { 
      title: "Capacity", 
      dataIndex: "capacity", 
      key: "capacity",
      render: (capacity, record) => {
        if (record.isUnlinkedRequest) return <Tag className="tag-pending">Requested to Join</Tag>;
        
        const dDate = record.deadline_date || (record.trip_date ? dayjs(record.trip_date).subtract(1, 'day').format('YYYY-MM-DD') : null);
        const dTime = record.deadline_time || "17:00:00";
        let isDeadlinePassed = false;
        if (dDate) {
          const deadline = dayjs(`${dDate} ${dTime}`, ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm", "YYYY-MM-DD h:mm a"]);
          isDeadlinePassed = dayjs().isAfter(deadline);
        }

        const isFull = capacity && capacity.split('/')[0] === capacity.split('/')[1];
        
        if (isDeadlinePassed || isFull) {
           return <Tag color="error">{capacity || "Unavailable"}</Tag>;
        }

        return <Tag color="default">{capacity || "N/A"}</Tag>;
      }
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button 
          type="text" 
          icon={<EyeOutlined />} 
          onClick={() => onViewTrip(record)}
        />
      ),
    }
  ];

  return (
    <Modal
      title={<Title level={5} style={{ color: 'white', margin: 0, fontSize: '16px' }}>List of Trips for {selectedDate ? selectedDate.format("MMMM D, YYYY") : ""}</Title>}
      open={isOpen}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
      ]}
      width={1000}
      closeIcon={<CloseOutlined style={{ color: "white" }} />}
      styles={{
        header: {
          backgroundColor: "#862633",
          padding: "16px 24px",
          borderRadius: "8px 8px 0 0",
          margin: "-20px -24px 24px -24px"
        },
        body: { maxHeight: '70vh', overflowY: 'auto', overflowX: 'hidden', margin: '0 -24px', padding: '0 24px' }
      }}
      className="ouber-modal white-close-icon"
    >
      <div style={{ marginBottom: "16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Text type="secondary" style={{ fontSize: '12px' }}>
          Select a trip to view details{isPastDate ? "." : " or request a custom schedule if none fit your needs."}
        </Text>
        {!isPastDate && (
          <Tooltip title={!hasActivePosition ? noPositionMessage : ""}>
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={onRequestTrip}
              disabled={!hasActivePosition}
            >
              Request Trip
            </Button>
          </Tooltip>
        )}
      </div>

      <Table 
        columns={columns} 
        dataSource={sortedDayTrips} 
        pagination={{ pageSize: 5 }}
        locale={{ emptyText: "No trips scheduled for this date." }}
      />
    </Modal>
  );
};

export default UserTripListModal;
